import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Browser from '@wetransfer/concorde-browser';
import Cookie from '@wetransfer/concorde-cookie';

import Upsell from './upsell';
import Link from './link';
import Email from './email';
import EmailVerificationUpsell from './email-verification-upsell';

import { userHasFreeTier } from '@helper/user';

import {
  shouldDisplayUpsellProgressPanel,
  openProgressPanel,
} from '@app/helper/progress-panel';

import Analytics from '@module/analytics';
import LaunchDarkly from '@module/launchdarkly';
import Router from '@module/router';
import Actions from '@app/store/action';
import Helper from '@app/helper';
import {
  COOKIE_TRANSFER_TYPE_LINK,
  COOKIE_TRANSFER_TYPE_EMAIL,
} from '@constant/cookie-transfer-types';

import { EXPERIMENT_UPSELL_SURVEY } from '@constant/experiments';
import { AppConfig } from '@app/core-bundle/config/app-config';

const upsellKeys = ['upsell_two', 'upsell_three'];

export const getUpsellKey = () => {
  if (Browser.platform('Mac')) {
    if (Helper.getWTSent() === 0) {
      // On first visit of Mac user always display macapp upsell (should be the last)
      return 'upsell_macapp';
    }
    upsellKeys.unshift('upsell_macapp');
  }

  if (LaunchDarkly.getVariant(EXPERIMENT_UPSELL_SURVEY)) {
    upsellKeys.unshift('upsell_survey');
  }

  // randomly display an upsell if nothing has returned earlier
  return upsellKeys[Math.floor(Math.random() * upsellKeys.length)];
};

export class Complete extends React.PureComponent {
  state = {
    manualCopy: false,
    copied: false,
    shouldDisplayUpsell: false,
    upsellKey: getUpsellKey(),
  };

  componentDidMount() {
    this.rememberInCookie();

    if (shouldDisplayUpsellProgressPanel(this.props.user)) {
      openProgressPanel();
    }
  }

  handleUpsellClick = () => {
    const { upsellKey } = this.state;

    Analytics.trackSnowplowEvent({
      category: 'upsell',
      action: 'click_upsell_button',
      label: upsellKey,
      property: 'upload_page',
    });
  };

  // Get the current transfer type and create a new Transfer
  onNewTransfer = () => {
    Analytics.trackSnowplowEvent({
      category: 'transfer',
      action: 'click_continue',
    });
    this.props.createSimilar();
  };

  rememberInCookie() {
    const { user, transfer } = this.props;
    // Secure these cookies on non-dev environments, set expiry to 365 days
    const cookieOptions = { secure: !__DEV__, days: 365 };
    // ttype is what current users have, so we can keep using their existing preference
    // when we release the site. ttype = transfer type.
    // For compatibility reasons.. we save it as a number.
    // TODO: REMOVE after rollout, we want sane cookies!
    let transferType = transfer.type;
    if (transferType === 'link') {
      transferType = COOKIE_TRANSFER_TYPE_LINK;
    } else {
      transferType = COOKIE_TRANSFER_TYPE_EMAIL;
    }
    Cookie.set(
      AppConfig.cookies.transferType.name,
      transferType,
      cookieOptions
    );

    // Remember recipients and sender email addresses
    if (transfer.type === 'email') {
      if (!user || !user.loggedIn) {
        let savedAddresses = Cookie.get('wt_to') || '';
        savedAddresses = savedAddresses.split(/\s*,\s*/);

        for (const email of transfer.recipients) {
          if (savedAddresses.indexOf(email.toLowerCase()) !== -1) {
            continue;
          }
          savedAddresses.push(email);
        }
        Cookie.set('wt_to', savedAddresses.join(','), cookieOptions);
      }

      Cookie.set('wt_from', transfer.sender, cookieOptions);
    }
  }

  displayUpsell = () => {
    const { user } = this.props;
    const { upsellKey } = this.state;
    const isFreeUser = userHasFreeTier(user);

    Analytics.trackSnowplowEvent({
      category: 'transfer',
      action: 'click_send_another',
    });

    if (user.loggedIn && !isFreeUser) {
      this.onNewTransfer();
    } else {
      this.setState({ shouldDisplayUpsell: true });

      Analytics.trackSnowplowEvent({
        category: 'upsell',
        action: 'show_page',
        label: upsellKey,
        property: 'upload_page',
      });
    }
  };

  navigateToFreeAccountSignUp = () => {
    Analytics.trackSnowplowEvent({
      category: 'transfer',
      action: 'click_create_free_account',
    });
    Router.navigateTo('/sign-up', false, {
      search: 'trk=WT202005_dont_verify_anonym_to_fa&tier=free&prefill=true',
    });
  };

  render() {
    const { transfer, user } = this.props;
    const { upsellKey, shouldDisplayUpsell } = this.state;

    let content = !user.loggedIn ? (
      <EmailVerificationUpsell
        onRequestClose={this.displayUpsell}
        onFreeAccountUpsellClick={this.navigateToFreeAccountSignUp}
        transfer={transfer}
      />
    ) : (
      <Email onRequestClose={this.displayUpsell} />
    );

    if (shouldDisplayUpsell) {
      content = (
        <Upsell
          upsellKey={upsellKey}
          onNewTransfer={this.onNewTransfer}
          onHandleUpsellClick={this.handleUpsellClick}
        />
      );
    } else if (transfer.type === 'link') {
      content = <Link onRequestClose={this.displayUpsell} />;
    }

    return (
      <div className="transfer__window uploader uploader--complete">
        {content}
      </div>
    );
  }
}

Complete.propTypes = {
  createSimilar: PropTypes.func.isRequired,
  session: PropTypes.object,
  transfer: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
};

const mapDispatchToProps = {
  createSimilar: Actions.Transfer.createSimilar,
};

export default connect(null, mapDispatchToProps)(Complete);
